<template>
    <GeneralDialogTemplate @close="closeDialog">
        <template slot="header">
            <span class="font-weight-medium">
                {{ formTitle }}
            </span>
        </template>
        <form>
            <v-row>
                <v-col class="pb-0 pl-5 pr-5" cols="12" sm="12" md="12">
                    <v-text-field
                        v-model="topic.name"
                        prepend-icon="bookmark"
                        :error-messages="topic.name_error"
                        :label="$t('Name of topic')"
                        outlined
                    />
                </v-col>

                <v-col v-if="isMonitoringEnabled" class="pb-0 pl-5 pr-5" cols="12">
                    <v-checkbox :input-value="topic.is_monitor"
                                :true-value="1"
                                :false-value="0"
                                color="primary"
                                @change="topic.is_monitor = ($event ? 1 : 0)">
                        <div slot="label" class="subheading">
                            {{ $t("Monitoring") }}
                        </div>
                    </v-checkbox>
                </v-col>

                <v-col v-if="isBillingEnabled" class="pb-8 pl-5 pr-5" cols="12">
                    <BillableButton
                        :value="topic.billable"
                        :show-label="false"
                        :label="topic.billable ? $t('Billable') : $t('Non-billable')"
                        @change="topic.billable = ($event ? 1 : 0)"
                    />
                    <div v-if="!topic.billable">{{ $t("New time entries will be marked as non-billable") }}</div>
                </v-col>
            </v-row>
        </form>
        <template slot="footer">
            <v-btn
                v-if="topic.id"
                text
                color="error"
                @click="onDeleteTopicClick"
            >
                {{ $t("Delete") }}
            </v-btn>
            <v-btn
                v-if="topic.id"
                text
                color="primary"
                @click="onReplaceClick"
            >
                {{ $t("Replace") }}
            </v-btn>
            <span style="color:white">{{ topic.id }}</span>
            <v-spacer/>
            <v-btn
                text
                color="primary"
                @click="closeDialog"
            >
                {{ $t("Cancel") }}
            </v-btn>
            <v-btn
                color="primary"
                class="white--text"
                @click="onSaveClick"
            >
                {{ $t("Save") }}
            </v-btn>
        </template>
        <EditTopicReplaceDialog
            v-if="isReplaceVisible"
            :company-id="topic.account_id"
            :current-topic-id="topic.id"
            :topics="topics"
            @close="isReplaceVisible = false"
        />
    </GeneralDialogTemplate>
</template>

<script>
import {computed, ref} from "@vue/composition-api";
import GeneralDialogTemplate from "../Dialog/GeneralDialogTemplate";
import {$t} from "../../i18n";
import {watch, reactive, toRefs} from "@vue/composition-api";
import AppModule from "../../store/modules/app";
import TopicsModule from "../../store/modules/topic";
import DialogModule from "../../store/modules/dialog";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import EditTopicReplaceDialog from "@/views/Dialog/EditTopicReplaceDialog";
import TopicModule from "@/store/modules/topic";
import BillableButton from "@/views/Task/TimeEntries/BillableButton.vue";
import AccountModule from "@/store/modules/account";

export default {
    name: "EditTopicDialog",
    components: {
        GeneralDialogTemplate,
        EditTopicReplaceDialog,
        BillableButton
    },
    props: {
        dialogIndex: {
            type: Number,
            default: null
        }
    },
    setup(props) {
        const state = reactive({
            topic: {
                name: "",
                name_error: null,
                billable: 1,
                is_monitor: 0,
            }
        });
        const {isFeatureEnabled} = AccountModule();
        const {dialogs, closeDialog, setConfirmOptions, openConfirmDialog} = DialogModule();
        const {deleteTopic, saveTopic} = TopicsModule();
        const {topics} = TopicModule();
        const {clearValidationErrors, handleErrorResponse} = AppModule();
        const dialogPayload = dialogs.value[props.dialogIndex].payload
        const isReplaceVisible = ref(false);

        const formTitle = computed(() => {
            if (state.topic.id) {
                return $t("Change topic");
            } else {
                return $t("Add new topic");
            }
        });
        watch(() => dialogPayload, () => {
            state.topic = Object.assign({
                name: "",
                name_error: null,
                billable: 1,
                is_monitor: 0,
            }, dialogPayload);
        });
        const onSaveClick = () => {
            clearValidationErrors(state.topic);

            saveTopic(state.topic)
                .then(function () {
                    closeDialog();
                })
                .catch(function (ex) {
                    handleErrorResponse(state.topic, ex);
                });
        };

        const onDeleteTopicConfirm = async () => {
            try {
                await deleteTopic(state.topic.id, 1);
                closeDialog()
            } catch (ex) {
                setConfirmOptions({
                    title: $t("Warning!"),
                    text: $t("This topic is in use. Do you wish to archive it?"),
                    confirm_text: $t("Archive"),
                    cancel_text: $t("Cancel"),
                    action: () => {
                        deleteTopic(state.topic.id, 0).catch(ex => errorDialog.showError(ex));
                        closeDialog()
                    }
                });
                openConfirmDialog();
            }
        };
        const onDeleteTopicClick = () => {
            setConfirmOptions({
                title: $t("Notice!"),
                text: $t("Are you sure that you want to delete this topic?"),
                confirm_text: $t("Yes"),
                cancel_text: $t("Cancel"),
                action: onDeleteTopicConfirm
            });
            openConfirmDialog();
        };

        const onReplaceClick = () => {
            isReplaceVisible.value = true
        }

        const isBillingEnabled = computed(() => isFeatureEnabled(state.topic.account_id, 'billing', false))
        const isMonitoringEnabled = computed(() => isFeatureEnabled(state.topic.account_id, 'monitoring', false))

        return {
            ...toRefs(state),
            closeDialog,
            formTitle,
            onDeleteTopicClick,
            onSaveClick,
            onReplaceClick,
            isReplaceVisible,
            topics,
            isBillingEnabled,
            isMonitoringEnabled,
        };
    }
};
</script>
